import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AssistantConversationDTO, AssistantConversationSummaryDTO, AssistantEditBody, AssistantLinkRef,
  AssistantMeBody, AssistantMeDTO, AssistantOnboardingBody, AssistantPendingDTO, AssistantResolvedObject, VoiceSampleBody,
  VoiceSampleDTO,
} from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { DEFAULT_APPEARANCE, DEFAULT_PERSONA_NAME, type PersonaAppearance } from '@contracts/persona';
import { api, ApiClientError } from '../../api/client';
import { usePreservedMutation, type PreservedMutation } from '../../api/query';

/**
 * The assistant's reads and its JSON writes (spec §6). A message, a confirm and a reject are not here: they may
 * answer an event stream, so they go through `readTurn(turnPath(id) | actionPath(id, kind), …)` (D71, D84).
 */

export const ASSISTANT_KEY = ['assistant'] as const;

/** `ASSISTANT_DISABLED` is a settled answer a screen shows (the server's own words), never retried. Here, not in a
 *  feature's api, so a page-independent section (S6 §11.3 `ConnectionsSection`) can read it. */
export const isAssistantOff = (error: unknown): error is ApiClientError =>
  error instanceof ApiClientError && error.code === 'ASSISTANT_DISABLED';
/** S8 §8.3: the person's own `/me` (prefs, persona, onboarding). */
export const ME_KEY = ['assistant', 'me'] as const;
export const CONVERSATIONS_KEY = ['assistant', 'conversations'] as const;
export const conversationKey = (id: Id) => ['assistant', 'conversation', id] as const;

/**
 * A confirm or a reject answered: the card leaves the conversation at once, wherever it was answered (the chat's card
 * or the Map's bar), so nothing still offers it while the resumed turn streams and the Map's ghosts go with it (P4 G7
 * review 2). A refusal brings it back: the turn's end reads the conversation again.
 */
export function dropPending(queryClient: QueryClient, conversationId: Id | null, actionId: Id): void {
  if (conversationId === null) return;
  queryClient.setQueryData<AssistantConversationDTO>(conversationKey(conversationId),
    (c) => (c === undefined ? c : { ...c, pending: c.pending.filter((p) => p.actionId !== actionId) }));
}

/** `/me` as the server answers it; `null` only for `403 ASSISTANT_DISABLED` (an older server, or a race). */
async function fetchOwnMe(): Promise<AssistantMeDTO | null> {
  try {
    return await api<AssistantMeDTO>('/api/assistant/me');
  } catch (e) {
    if (isAssistantOff(e)) return null;
    throw e;
  }
}

const OWN_ME = { queryKey: ME_KEY, queryFn: fetchOwnMe, retry: false, staleTime: 60_000 } as const;

/**
 * S8 D129: the person's own `/me` as it is, `aiOff` included: the onboarding gate and `/welcome` read it, so a person
 * with the assistant off still has their steps (D115). `null` = `403 ASSISTANT_DISABLED`, or the UI tests' "unknown".
 */
export function useOwnMe(): UseQueryResult<AssistantMeDTO | null, ApiClientError> {
  return useQuery<AssistantMeDTO | null, ApiClientError>(OWN_ME);
}

/**
 * `null` = the assistant is off for this person (S8 D114 `aiOff`, or `403 ASSISTANT_DISABLED`): a settled "no" that
 * neither retries nor toasts, so the button stays hidden (D125). The same query as `useOwnMe`, seen through `select`.
 */
export function useMe(): UseQueryResult<AssistantMeDTO | null, ApiClientError> {
  return useQuery<AssistantMeDTO | null, ApiClientError, AssistantMeDTO | null>({
    ...OWN_ME, select: (me) => (me !== null && me.aiOff === null ? me : null),
  });
}

/**
 * The reader's own persona name (S8 D9), for the owner's own pages; `DEFAULT_PERSONA_NAME` while it loads or with the
 * assistant off. Shares `useMe`'s query, so a page that already reads `/me` costs nothing more.
 */
export function usePersonaName(): string {
  return useMe().data?.personaName ?? DEFAULT_PERSONA_NAME;
}

/**
 * The reader's own persona look (S8 §9, D93), for every figure that stands for the reader's own assistant; the
 * default look while `/me` loads or with the assistant off. It reads `ME_KEY`, so a saved persona redraws every own
 * figure at once. Never pass it to a figure of anyone else's assistant: theirs is the generic ✦ (D92).
 */
export function useOwnAppearance(): PersonaAppearance {
  return useMe().data?.appearance ?? DEFAULT_APPEARANCE;
}

export function useConversations(enabled: boolean): UseQueryResult<AssistantConversationSummaryDTO[], ApiClientError> {
  return useQuery<AssistantConversationSummaryDTO[], ApiClientError>({
    queryKey: CONVERSATIONS_KEY, queryFn: () => api<AssistantConversationSummaryDTO[]>('/api/assistant/conversations'),
    enabled,
  });
}

/**
 * One conversation. `pollUntil` (D121): after a stream cut before `turn_done`, the conversation is read again every
 * 2 s while the server says the turn still runs, up to that instant.
 */
export function useConversation(id: Id | null, pollUntil: number | null = null):
  UseQueryResult<AssistantConversationDTO, ApiClientError> {
  return useQuery<AssistantConversationDTO, ApiClientError>({
    queryKey: conversationKey(id ?? ''),
    queryFn: () => api<AssistantConversationDTO>(`/api/assistant/conversations/${id ?? ''}`),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data?.running === true && pollUntil !== null && Date.now() < pollUntil
      ? POLL_MS : false),
  });
}
export const POLL_MS = 2000;
export const POLL_FOR_MS = 300_000;

export const createConversation = (requestId: Id): Promise<AssistantConversationSummaryDTO> =>
  api<AssistantConversationSummaryDTO>('/api/assistant/conversations', { method: 'POST', body: {}, requestId });

export const resolveObjects = (refs: AssistantLinkRef[]): Promise<AssistantResolvedObject[]> =>
  api<AssistantResolvedObject[]>('/api/assistant/objects/resolve', { method: 'POST', body: { refs } });

export const editAction = (actionId: Id, body: AssistantEditBody, requestId: Id): Promise<AssistantPendingDTO> =>
  api<AssistantPendingDTO>(`/api/assistant/actions/${actionId}/edit`, { method: 'POST', body, requestId });

export const undoAction = (actionId: Id, requestId: Id): Promise<{ actionId: Id; status: 'undone' }> =>
  api<{ actionId: Id; status: 'undone' }>(`/api/assistant/actions/${actionId}/undo`,
    { method: 'POST', body: {}, requestId });

/** S8 D113: one onboarding move (a step, "Để sau", or the finish); answers the whole `/me`, for `ME_KEY`. */
export const putOnboarding = (body: AssistantOnboardingBody, requestId: Id): Promise<AssistantMeDTO> =>
  api<AssistantMeDTO>('/api/assistant/me/onboarding', { method: 'PUT', body, requestId });

/**
 * One onboarding move at a time (T4): a success puts the answered `/me` in `ME_KEY` before the caller moves on, so the
 * gate already sees "deferred" or "finished" when the person lands on `/`.
 */
export function useOnboarding(): PreservedMutation<AssistantOnboardingBody, AssistantMeDTO> {
  const queryClient = useQueryClient();
  return usePreservedMutation(useCallback(async (body: AssistantOnboardingBody, requestId: Id) => {
    const me = await putOnboarding(body, requestId);
    queryClient.setQueryData(ME_KEY, me);
    return me;
  }, [queryClient]));
}

export const saveMe = (body: AssistantMeBody, requestId: Id): Promise<AssistantMeDTO> =>
  api<AssistantMeDTO>('/api/assistant/me', { method: 'PUT', body, requestId });

/** S8 D119: "Nghe thử thật" — the default model speaks one paragraph as the drafted persona; `left` is today's rest. */
export const voiceSample = (body: VoiceSampleBody): Promise<VoiceSampleDTO> =>
  api<VoiceSampleDTO>('/api/assistant/me/voice-sample', { method: 'POST', body });

export const turnPath = (conversationId: Id): string => `/api/assistant/conversations/${conversationId}/turns`;
export const actionPath = (actionId: Id, kind: 'confirm' | 'reject'): string =>
  `/api/assistant/actions/${actionId}/${kind}`;
